import { useTranslation } from 'react-i18next'
import { formatCurrency } from '@/lib/formatters'
import type { OrderItem } from '../types'

interface OrderItemsListProps {
  items: OrderItem[]
  compact?: boolean
}

export function OrderItemsList({ items, compact = false }: OrderItemsListProps) {
  const { t } = useTranslation('orders')

  if (items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground italic">
        {t('order.noItems')}
      </p>
    )
  }

  if (compact) {
    return (
      <div className="text-sm text-muted-foreground">
        {items.length} {items.length === 1 ? t('order.item', { count: 1 }) : t('order.item_plural', { count: items.length })}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
        {t('items.title')}
      </h4>

      <div className="space-y-2">
        {items.map((item) => (
          <OrderItemRow key={item.id} item={item} />
        ))}
      </div>
    </div>
  )
}

function OrderItemRow({ item }: { item: OrderItem }) {
  const { t } = useTranslation('orders')
  const quantity = item.quantity ?? 1

  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-border last:border-0">
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="font-medium text-sm text-muted-foreground">
            {t('items.quantity', { count: quantity })}
          </span>
          <span className="font-medium truncate">{item.name}</span>
        </div>

        {/* Variations */}
        {item.variations.length > 0 && (
          <div className="mt-1 ms-6 space-y-0.5">
            {item.variations.map((variation, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between text-sm text-muted-foreground"
              >
                <span className="truncate">
                  {variation.menuItemVariationCategoryName && (
                    <span className="text-xs uppercase opacity-70">
                      {variation.menuItemVariationCategoryName}:{' '}
                    </span>
                  )}
                  {variation.name}
                </span>
                {variation.takeawayPrice > 0 && (
                  <span className="ms-2 text-xs">
                    +{formatCurrency(variation.takeawayPrice)}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Extra items */}
        {item.extraItems.length > 0 && (
          <div className="mt-1 ms-6 text-sm text-muted-foreground">
            {item.extraItems.map((extra, idx) => (
              <span key={idx}>
                {idx > 0 && ', '}
                +{extra.name}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="text-end shrink-0">
        <div className="font-medium">
          {formatCurrency(item.price * quantity)}
        </div>
        {item.discountPrice < item.price && (
          <div className="text-sm text-green-600">
            {formatCurrency(item.discountPrice * quantity)}
          </div>
        )}
      </div>
    </div>
  )
}
